import { ModalTemplateWrapper } from '../ModalTemplateWrapper/ModalTemplateWrapper.js';
import { type CommonTemplateProps } from '../types.js';
import { type ReactNode, useCallback } from 'react';

type ResourceType = 'project' | 'service';

type SaveAsDraftModalProps = CommonTemplateProps & {
  readonly isMovingTo?: boolean;
  readonly isSavingAsDraft?: boolean;
  readonly onSaveAsDraft: (closeModal: VoidFunction) => void;
  readonly resourceType: ResourceType;
};

const getSubHeaderText = (resourceType: ResourceType): ReactNode => {
  return `A draft ${resourceType} is only visible to you. You can see it on your profile, but it is hidden from others.`;
};

const getHeading = (
  resourceType: ResourceType,
  isMovingTo: boolean,
): string => {
  if (isMovingTo) {
    return `Move ${resourceType} to drafts?`;
  }

  return `Save ${resourceType} as draft?`;
};

export const SaveAsDraftModal = ({
  closeModal,
  isMovingTo = false,
  isOpen,
  isSavingAsDraft = false,
  onSaveAsDraft,
  resourceType,
}: SaveAsDraftModalProps) => {
  const handleSaveAsDraft = useCallback(() => {
    onSaveAsDraft(closeModal);
  }, [closeModal, onSaveAsDraft]);

  const heading = getHeading(resourceType, isMovingTo);

  return (
    <ModalTemplateWrapper
      ariaLabel={heading}
      ctaOptions={{
        primaryCTA: {
          handleOnClick: handleSaveAsDraft,
          isDisabled: isSavingAsDraft,
          isLoading: isSavingAsDraft,
          label: isMovingTo ? 'Move to drafts' : 'Save as draft',
          variant: 'V2Primary',
        },
        secondaryCTA: {
          handleOnClick: closeModal,
          label: 'Cancel',
          variant: 'V2TertiaryOutline',
        },
      }}
      desktopModalSize="md"
      headerText={heading}
      isOpen={isOpen}
      onClose={closeModal}
      subheaderText={getSubHeaderText(resourceType)}
    />
  );
};
